import { Component } from '@theme/component';

/**
 * An opt-in add-on product shown next to the main product.
 *
 * The theme's product form posts a single `id` + `quantity` pair and its
 * success handler throws when `id` is missing, so the add-on cannot ride along
 * as an `items[]` entry. It also binds its submit handler at document capture,
 * which runs before anything this component could attach to the form, so the
 * main product is already on its way by the time we see the submit — stopping
 * or replaying it only adds the main product twice.
 *
 * So the add-on is simply sent alongside, and the cart is told to refresh once
 * it lands, in case the drawer rendered before that.
 */
class ProductAddonComponent extends Component {
  #form = null;

  connectedCallback() {
    super.connectedCallback?.();
    this.#form = this.productForm;
    this.#form?.addEventListener('submit', this.#handleFormSubmit);
    this.#reflectSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#form?.removeEventListener('submit', this.#handleFormSubmit);
  }

  /* ------------------------------------------------------------------ refs */

  get toggle() {
    return this.refs?.toggle ?? this.querySelector('.product-addon__input');
  }

  get selected() {
    return Boolean(this.toggle?.checked);
  }

  get productForm() {
    return (
      this.closest('form[action*="/cart/add"]') ??
      this.closest('.shopify-section, product-form-component, .product-information')?.querySelector(
        'form[action*="/cart/add"]'
      ) ??
      null
    );
  }

  /* -------------------------------------------------------------- handlers */

  /** Bound through `on:change="/handleToggle"`. */
  handleToggle() {
    this.#reflectSelection();
    this.dispatchEvent(
      new CustomEvent('product-addon:change', {
        bubbles: true,
        detail: { selected: this.selected, variantId: this.dataset.variantId },
      })
    );
  }

  /** Bound through `on:click="/toggleInfo"`. */
  toggleInfo(event) {
    // The button sits inside the label, so let neither the label nor the card
    // act on this click.
    event.preventDefault();
    event.stopPropagation();

    const wrap = event.target.closest('.product-addon__info-wrap');
    if (!wrap) return;

    const open = !wrap.hasAttribute('data-open');
    wrap.toggleAttribute('data-open', open);
    wrap.querySelector('.product-addon__info')?.setAttribute('aria-expanded', String(open));

    if (open) {
      document.addEventListener('click', this.#closeInfo, { once: true });
    }
  }

  #closeInfo = () => {
    for (const wrap of this.querySelectorAll('.product-addon__info-wrap[data-open]')) {
      wrap.removeAttribute('data-open');
      wrap.querySelector('.product-addon__info')?.setAttribute('aria-expanded', 'false');
    }
  };

  #handleFormSubmit = () => {
    if (!this.selected) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    // Deliberately neither prevented nor replayed: the theme is already adding
    // the main product, and interfering doubles it.
    this.#addAddon(variantId);
  };

  async #addAddon(variantId) {
    const root = window.Shopify?.routes?.root ?? '/';
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!response.ok) {
        // The main product is still worth adding, so only report and move on.
        console.warn('[product-addon] could not add the add-on:', await response.text());
        return;
      }

      // The theme may have rendered the drawer before this landed.
      const cart = await fetch(`${root}cart.js`).then((res) => res.json());
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: 'product-addon-component', data: { itemCount: cart.item_count } },
        })
      );
    } catch (error) {
      console.warn('[product-addon] could not add the add-on:', error);
    }
  }

  #reflectSelection() {
    this.classList.toggle('product-addon--selected', this.selected);
  }
}

if (!customElements.get('product-addon-component')) {
  customElements.define('product-addon-component', ProductAddonComponent);
}
